import { classNames, Component, h, tag } from "omi";

import globalStyles from "@/globals/styles.css?inline";
import { NWIcon } from "@/wc/components";
import { tailwind } from "@/tailwind";

export type CheckboxProps = {
  size?: "sm" | "md";
  checked: boolean;
  disabled?: boolean;
  tabIndex?: number;
  onChange?: (checked: boolean) => void;
};

@tag("nw-checkbox")
export class NWCheckbox extends Component<CheckboxProps> {
  static css = [tailwind, globalStyles];
  static propTypes = {
    checked: Boolean,
    disabled: Boolean,
    tabIndex: Number,
    size: String,
  };

  static defaultProps = {
    checked: false,
    disabled: false,
    tabIndex: 0,
    size: "md",
  };

  private handleOnClick = (event: Event) => {
    if (this.props.disabled) return;
    event.preventDefault();
    event.stopPropagation();

    const updatedCheckedState = !this.props.checked;

    if (this.props.onChange) {
      this.props.onChange(updatedCheckedState);
    }

    this.fire("checkbox-change", { checked: updatedCheckedState });
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (this.props.disabled) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.handleOnClick(event);
    }
  };

  private getIconSize = () => {
    switch (this.props.size) {
      case "sm":
        return 12;
      default:
      case "md":
        return 16;
    }
  };

  render() {
    const { checked, disabled, tabIndex = 0, size = "md" } = this.props;
    const iconSize = this.getIconSize();

    return (
      <div class="m-1.5 w-fit flex items-center">
        <input type="checkbox" id="checkbox" name="checkbox" value="checkbox" class="hidden" />
        <label
          for="checkbox"
          class={classNames("flex items-start justify-center gap-x-3 px-1", {
            "opacity-50": disabled,
          })}
          role="checkbox"
          aria-checked={checked}
          aria-disabled={disabled}
        >
          <div
            onClick={this.handleOnClick}
            onKeyDown={this.handleKeyDown}
            tabIndex={disabled ? -1 : tabIndex}
            class={classNames(
              "p-0.5 my-1 group select-none border rounded-[4px] active:ring-4 flex items-center justify-center transition-all shrink-0",
              {
                "bg-green-400 border-green-400 active:ring-green-300/25 hover:bg-green-500": checked && !disabled,
                "bg-white active:ring-gray-100 border-gray-300 hover:bg-gray-50": !checked && !disabled,
                "bg-secondary border-gray-300": disabled,
              },
              {
                "w-4 h-4": size === "sm",
                "w-5 h-5": size === "md",
              },
              {
                "cursor-pointer": !disabled,
                "cursor-not-allowed": disabled,
              },
            )}
          >
            <NWIcon name="checkbox-tick" className="shrink-0" width={iconSize} height={iconSize}></NWIcon>
          </div>
          <div
            class={classNames("w-fit select-auto transition-all duration-200 text-lg !m-0", {
              "line-through decoration-from-font text-tertiary": checked,
              "text-secondary": !checked,
            })}
          >
            <slot></slot>
          </div>
        </label>
      </div>
    );
  }
}
